import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, FileDown, ShieldCheck } from 'lucide-react'
import { authApi, legalDocumentsApi, LegalDocumentInfo } from '../services/api'
import { useOnboardingStatus } from '../hooks/useOnboardingStatus'

export default function AcceptTermsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { documentsRequiringReacceptance } = useOnboardingStatus()

  const [acceptTerms, setAcceptTerms] = useState(false)
  const [acceptPrivacy, setAcceptPrivacy] = useState(false)
  const [acceptB2B, setAcceptB2B] = useState(false)
  const [acceptDPA, setAcceptDPA] = useState(false)
  const [marketingEmail, setMarketingEmail] = useState(false)
  const [marketingPhone, setMarketingPhone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: legalDocs } = useQuery({
    queryKey: ['legal-documents'],
    queryFn: () => legalDocumentsApi.getCurrent().then(r => r.data),
  })

  const getLegalDoc = (slug: string): LegalDocumentInfo | undefined =>
    legalDocs?.find(d => d.slug === slug)

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`
  }

  const isDocChanged = (slug: string) =>
    documentsRequiringReacceptance?.includes(slug) ?? false

  const acceptMutation = useMutation({
    mutationFn: () =>
      authApi.acceptTerms({
        acceptTerms,
        acceptPrivacy,
        acceptB2B,
        acceptDPA,
        marketingEmail,
        marketingPhone,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
      navigate('/', { replace: true })
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setError(err.response?.data?.error || 'Wystąpił błąd. Spróbuj ponownie.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!acceptTerms || !acceptPrivacy || !acceptB2B || !acceptDPA) {
      setError('Zaznacz wszystkie wymagane zgody.')
      return
    }

    acceptMutation.mutate()
  }

  const changedDocs = legalDocs?.filter(d => isDocChanged(d.slug)) ?? []

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <ShieldCheck className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Zaktualizowane dokumenty prawne
            </h1>
            <p className="text-gray-500 mt-2">
              Dokumenty prawne LexTool zostały zaktualizowane. Aby kontynuować korzystanie z aplikacji, zaakceptuj nowe wersje.
            </p>
          </div>

          {/* Changed documents info */}
          {changedDocs.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-amber-800 mb-2">Zmienione dokumenty:</p>
              <ul className="space-y-1">
                {changedDocs.map(doc => (
                  <li key={doc.slug} className="flex items-center justify-between text-sm">
                    <span className="text-amber-700">
                      {doc.displayName} — wersja {doc.version} ({formatDate(doc.effectiveDate)})
                    </span>
                    <a
                      href={legalDocumentsApi.getPdfUrl(doc.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      PDF
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Required: Regulamin + AUP */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={e => setAcceptTerms(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <span className="text-red-500">*</span>{' '}
                Akceptuję{' '}
                <a href={getLegalDoc('regulamin')?.webUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Regulamin
                  {getLegalDoc('regulamin') && (
                    <span className="text-gray-400 text-xs ml-1">
                      (wersja {getLegalDoc('regulamin')!.version})
                    </span>
                  )}
                </a>
                {' '}oraz{' '}
                <a href={getLegalDoc('aup')?.webUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Politykę Dopuszczalnego Użycia (AUP)
                  {getLegalDoc('aup') && (
                    <span className="text-gray-400 text-xs ml-1">
                      (wersja {getLegalDoc('aup')!.version})
                    </span>
                  )}
                </a>
                .
              </span>
            </label>

            {/* Required: Privacy policy */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptPrivacy}
                onChange={e => setAcceptPrivacy(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <span className="text-red-500">*</span>{' '}
                Zapoznałem/am się z{' '}
                <a href={getLegalDoc('privacy_policy')?.webUrl || '#'} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Polityką prywatności
                  {getLegalDoc('privacy_policy') && (
                    <span className="text-gray-400 text-xs ml-1">
                      (wersja {getLegalDoc('privacy_policy')!.version})
                    </span>
                  )}
                </a>
                {' '}i akceptuję zasady przetwarzania moich danych osobowych.
              </span>
            </label>

            {/* Required: B2B */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptB2B}
                onChange={e => setAcceptB2B(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <span className="text-red-500">*</span>{' '}
                Oświadczam, że działam jako przedsiębiorca (B2B) i jestem umocowany/a do zawarcia umowy w imieniu podmiotu wskazanego w formularzu.
              </span>
            </label>

            {/* Required: DPA */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptDPA}
                onChange={e => setAcceptDPA(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                <span className="text-red-500">*</span>{' '}
                Przyjmuję do wiadomości, że w przypadku wprowadzania danych osobowych osób trzecich do Usługi, zostanie zawarta{' '}
                <a href={legalDocumentsApi.getPdfUrl('dpa')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Umowa powierzenia przetwarzania danych (DPA)
                  {getLegalDoc('dpa') && (
                    <span className="text-gray-400 text-xs ml-1">
                      (wersja {getLegalDoc('dpa')!.version})
                    </span>
                  )}
                </a>
                {' '}stanowiąca załącznik do Regulaminu.
              </span>
            </label>

            {/* Optional: Marketing email */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingEmail}
                onChange={e => setMarketingEmail(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Wyrażam zgodę na otrzymywanie informacji handlowych drogą elektroniczną (e-mail) od LexTool sp. z o.o.
              </span>
            </label>

            {/* Optional: Marketing phone */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingPhone}
                onChange={e => setMarketingPhone(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Wyrażam zgodę na kontakt telefoniczny / SMS w celach marketingowych przez LexTool sp. z o.o.
              </span>
            </label>

            <p className="text-xs text-gray-400">
              <span className="text-red-500">*</span> Pola wymagane
            </p>

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={acceptMutation.isPending}
              className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Zapisywanie...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  Akceptuję i kontynuuję
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
